import { createContext, useContext, useLayoutEffect } from "react";

/**
 * The site has two chromes (redesign Stage 2, F1): the public site's, and one signed-in "making"
 * chrome built on the W1 account header (My Library, Use your voucher, Help and the account
 * menu), with a slim making bar on a book's pages: the book, the step it's on, and the way back
 * to My Library. A page that knows its book says so with `useMakingTask`.
 */
export type MakingTask = {
  /** The book being made, when there is one: Help then carries it. */
  bookId?: string;
  /** The kind of book, in the family's own words ("Your bedtime book"). */
  kind?: string;
  /** The book's working title, or what the page is ("Photo library"). */
  title?: string;
  /** The step it's on, in the customer's words ("Step 3 of 7 · The words"). */
  step?: string;
  /** The words Help files the step under ("The words"); the step itself when absent. */
  helpStep?: string;
  /** The page of the book being read, for Help. */
  page?: number;
  /** The way back; My Library unless the page came from somewhere nearer. */
  exit?: { to: string; label: string };
};

export const MakingTaskContext = createContext<(task: MakingTask | null) => void>(() => {});

/** Shows this page's book and step in the making bar while the page is shown. */
export function useMakingTask(task: MakingTask | null) {
  const setTask = useContext(MakingTaskContext);
  const key = JSON.stringify(task);
  useLayoutEffect(() => {
    setTask(key === "null" ? null : (JSON.parse(key) as MakingTask));
    return () => setTask(null);
  }, [setTask, key]);
}

/**
 * Addresses where people make, manage and get help with books, and the steps into their account:
 * these always take the making chrome, signed in or not (signed out, it offers Sign in).
 */
const makingPaths = [
  /^\/app(?:\/|$)/i,
  /^\/support(?:\/|$)/i,
  /^\/checkout\//i,
  /^\/sign-in(?:\/|$)/i,
  /^\/redeem\/?$/i,
  /^\/resume\/?$/i,
  /^\/invitations\/accept\/?$/i,
  /^\/unsubscribe\/?$/i,
];

/**
 * Which chrome the shell draws on a page that doesn't draw its own. Once someone is signed in,
 * every page takes the making chrome, the home page's front door included (C2, 2026-11-02): one
 * header after sign-in. The front door keeps its own colours (`MakingChrome`'s `dark`).
 */
export function shellChrome(pathname: string, signedIn: boolean): "making" | "public" {
  if (makingPaths.some((pattern) => pattern.test(pathname))) return "making";
  return signedIn ? "making" : "public";
}

/**
 * Steps someone may take before they have an account here, each a task of its own (W3 Rounds
 * 10–11): signed out, the page keeps the brand and names its task instead of offering the
 * signed-in header. Signed in, it takes the shared header like any other page.
 */
const signedOutTasks: ReadonlyArray<[RegExp, string]> = [
  [/^\/resume\/?$/i, "Private memory prompt"],
  [/^\/invitations\/accept\/?$/i, "Private family invitation"],
  [/^\/checkout\/success\/?$/i, "Your book voucher"],
];

/** The task a signed-out visitor is on at this address, if it is one of those steps. */
export function signedOutTaskLabel(pathname: string, signedIn: boolean): string | null {
  if (signedIn) return null;
  return signedOutTasks.find(([pattern]) => pattern.test(pathname))?.[1] ?? null;
}

/** True for the public site's own pages, which keep its footer whoever is reading. */
export function publicSitePage(pathname: string) {
  return !makingPaths.some((pattern) => pattern.test(pathname));
}

/**
 * Redesigned pages draw their own header and footer, so the app shell must leave them out.
 * Known redesigned addresses are recognised from the path (`redesignOwnsPage`), and every
 * redesigned page also announces itself here before it paints. That covers the not-found page,
 * which can sit at any address.
 */
export const PageChromeContext = createContext<(owned: boolean) => void>(() => {});

export function usePageOwnsChrome() {
  const setOwned = useContext(PageChromeContext);
  useLayoutEffect(() => {
    setOwned(true);
    return () => setOwned(false);
  }, [setOwned]);
}

const redesignedPublicPaths = [
  /^\/books\/?$/i,
  /^\/books\/[^/]+\/?$/i,
  /^\/demo(?:\/[^/]+)?\/?$/i,
  /^\/how-it-works\/?$/i,
  /^\/faq\/?$/i,
  /^\/gift\/?$/i,
  /^\/pricing\/?$/i,
  /^\/bedtime-story-demo\/?$/i,
  /^\/qvc\/bedtime-rehearsal\/?$/i,
  /^\/checkout\/(?!success\/?$)[^/]+\/?$/i,
  /^\/sign-in(?:\/complete)?\/?$/i,
  /^\/app\/?$/i,
  /^\/app\/account\/?$/i,
  /^\/app\/new\/?$/i,
  /^\/redeem\/?$/i,
  /^\/support(?:\/approve)?\/?$/i,
  /^\/unsubscribe\/?$/i,
  /^\/app\/books\/[^/]+\/settings\/?$/i,
  // A book's people and invitations (E11).
  /^\/app\/books\/[^/]+\/family\/?$/i,
  // A printed copy's order. (A print review claims the chrome itself: Bedtime's cover and edition
  // steps at the same address keep the site's.)
  /^\/app\/physical-orders\/[^/]+\/?$/i,
];

/** True when the redesign draws this public page, header and footer included. Paths match without
 * regard to case, as the router does. */
export function redesignOwnsPage(pathname: string) {
  return redesignedPublicPaths.some((pattern) => pattern.test(pathname));
}

/** `useMakingTask` as an element, for a page whose book and step are known only where it draws. */
export function MakingTaskMark({ task }: { task: MakingTask | null }): null {
  useMakingTask(task);
  return null;
}
